'use client';

import { useSupabase } from '@/components/auth/auth-provider';
import { AppModal } from '../app-modal';
import { useWallet } from '@solana/wallet-adapter-react';
import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const { session: session } = useSupabase();
  const { connected, publicKey } = useWallet();
  const { setVisible } = useWalletModal();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Auto-close modal when user is authenticated
    if (session) {
      onClose();
    }
  }, [session, onClose])

  const handleWalletConnect = () => {
    setVisible(true);
  }

  const handleWeb3Login = async () => {
    if (!connected || !publicKey) {
      alert('Please connect your wallet first');
      return;
    }

    setIsConnecting(true);
    try {
      const walletAddress = publicKey.toBase58();
      console.log('🔄 Authenticating with _wallet:', walletAddress.slice(0, 8) + '...');

      // Wallet-signin removed - using username/password login only
      alert('Wallet login is disabled. Please use username/password login.');
      setIsConnecting(false);
      return;

      // Web3 wallet login removed - username/password only
    } catch (error) {
      console.error('❌ Error with Web3 login:', error);
      alert('Authentication failed. Please try again.');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Use backend API directly (Vite doesn't have Next.js API routes)
      const { getBackendUrl, getAuthHeaders } = await import('@/lib/api-helpers');
      const backendUrl = getBackendUrl();
      const endpoint = isSignUp ? `${backendUrl}/api/auth/signup` : `${backendUrl}/api/auth/signin`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ username, password, ...(isSignUp && email ? { email } : {}) }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Authentication failed');
      }

      const data = await response.json();

      // Handle backend auth token
      if (data.token) {
        if (typeof window !== 'undefined') {
          localStorage.setItem('runara_auth_token', data.token);
        }
        // Trigger session refresh
        window.dispatchEvent(new Event('auth-state-changed'));
      }

      // Close modal and refresh page to update auth state
      onClose();
      // Small delay to ensure token is stored before reload
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title="Login / Sign Up"
      description="Enter your username and password to continue."
    >
      <div className="p-4 space-y-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-900/50 border border-red-700 rounded text-red-200 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={isLoading}
              placeholder="Enter your username"
            />
          </div>

          {isSignUp && (
            <div className="space-y-2">
              <Label htmlFor="email">Email (optional)</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                placeholder="Enter your email"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              placeholder="Enter your password"
              minLength={6}
            />
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Loading...' : isSignUp ? 'Sign Up' : 'Sign In'}
          </Button>

          <div className="text-center text-sm">
            <button
              type="button"
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-blue-400 hover:text-blue-300 underline"
              disabled={isLoading}
            >
              {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>
          </div>
        </form>
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              Or continue with
            </span>
          </div>
        </div>
        <div className="space-y-3">
          {!connected ? (
            <div className="text-center">
              <Button onClick={handleWalletConnect} className="w-full">
                🔗 Connect Wallet
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Connect your Solana wallet first to enable Web3 gaming
              </p>
            </div>
          ) : (
            <div className="text-center">
              <div className="text-sm text-green-600 mb-2">
                ✅ Wallet Connected: {publicKey?.toBase58().slice(0, 8)}...
              </div>
              <Button
                onClick={handleWeb3Login}
                className="w-full"
                disabled={isConnecting}
              >
                {isConnecting ? 'Authenticating...' : '🎮 Enter Game with Wallet'}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Create account or sign in with your connected wallet
              </p>
            </div>
          )}
        </div>
      </div>
    </AppModal>
  );
}
